'use client';

import { Mail, ChevronDown, Settings, LogOut, Home } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { Session } from 'next-auth';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Authenticated navigation for logged-in users
export function NosemailAuthenticatedNavigation({ session }: { session: Session | null }) {
	const pathname = usePathname();

	const getInitials = (name?: string | null, email?: string | null) => {
		if (name) {
			return name
				.split(' ')
				.map((n) => n[0])
				.join('')
				.toUpperCase()
				.slice(0, 2);
		}
		if (email) {
			return email.slice(0, 2).toUpperCase();
		}
		return 'U';
	};

	return (
		<nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center h-16">
					{/* Logo and Dashboard Navigation */}
					<div className="flex items-center space-x-8">
						<Link
							href="/"
							className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
						>
							<div className="p-1 bg-emerald-50 rounded-lg">
								<Mail className="h-6 w-6 text-emerald-600" />
							</div>
							<span className="text-xl font-semibold text-gray-900">NOS Email</span>
						</Link>

						{/* Dashboard Navigation */}
						<div className="hidden md:flex items-center space-x-1 bg-gray-50 rounded-lg p-1">
							<Link
								href="/"
								className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
									!pathname?.includes('/mailboxes')
										? 'bg-white text-emerald-600 shadow-sm'
										: 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
								}`}
							>
								Domains
							</Link>
							<Link
								href="/mailboxes"
								className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
									pathname?.includes('/mailboxes')
										? 'bg-white text-emerald-600 shadow-sm'
										: 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
								}`}
							>
								Mailboxes
							</Link>
						</div>
					</div>

					{/* Right side navigation */}
					<div className="flex items-center space-x-4">
						{/* User Menu */}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									className="flex items-center space-x-2 h-10 px-3 hover:bg-gray-50"
								>
									<Avatar className="h-8 w-8">
										<AvatarImage
											src={session?.user?.image || ''}
											alt={session?.user?.name || ''}
										/>
										<AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-medium">
											{getInitials(session?.user?.name, session?.user?.email)}
										</AvatarFallback>
									</Avatar>
									<div className="hidden sm:block text-left">
										<div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
											{session?.user?.name ||
												session?.user?.email?.split('@')[0]}
										</div>
									</div>
									<ChevronDown className="h-4 w-4 text-gray-400" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-56">
								<div className="px-3 py-2">
									<p className="text-sm font-medium text-gray-900">
										{session?.user?.name || 'User'}
									</p>
									<p className="text-xs text-gray-500 truncate">
										{session?.user?.email}
									</p>
								</div>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<Link
										href="/settings"
										className="flex items-center space-x-2 cursor-pointer"
									>
										<Settings className="h-4 w-4" />
										<span>Settings</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild className="md:hidden">
									<Link
										href="/"
										className="flex items-center space-x-2 cursor-pointer"
									>
										<Mail className="h-4 w-4" />
										<span>Domains</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild className="md:hidden">
									<Link
										href="/mailboxes"
										className="flex items-center space-x-2 cursor-pointer"
									>
										<Mail className="h-4 w-4" />
										<span>Mailboxes</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={async () => {
										await signOut({ redirect: false });
										// Stay on the same domain by using relative path
										window.location.href = '/login';
									}}
									className="flex items-center space-x-2 cursor-pointer text-red-600 focus:text-red-600"
								>
									<LogOut className="h-4 w-4" />
									<span>Sign Out</span>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</div>
		</nav>
	);
}
